/** @jsxImportSource @emotion/react */
import React, { useRef, useState, useEffect } from "react";
import { css } from "@emotion/react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faTimes } from "@fortawesome/free-solid-svg-icons";

import Colors, { shadeColor } from "styles/colors";
import { useSearchTypeahead } from "pages/reports/bi-dashboard-next/hooks/useSearchTypeahead";
import { ReportTypeaheadDTO } from "pages/reports/bi-dashboard-next/models/SearchTypeaheadDTO";
import { DistinctTagDTO } from "pages/reports/bi-dashboard-next/models/DistinctTagDTO";

interface SearchInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSelectReport?: (report: ReportTypeaheadDTO) => void;
  onSelectTag?: (tag: DistinctTagDTO) => void;
}

export const SearchInput: React.FC<SearchInputProps> = (props) => {
  const { value, onChange, onSelectReport, onSelectTag, ...restProps } = props;
  const [focused, setFocused] = useState(false);
  const [showTypeahead, setShowTypeahead] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const typeaheadRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation("reports");

  const { isLoading, data } = useSearchTypeahead(value || "");

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        typeaheadRef.current &&
        !typeaheadRef.current.contains(event.target as Node) &&
        !inputRef.current?.contains(event.target as Node)
      ) {
        setShowTypeahead(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (
      data &&
      focused &&
      (data.matchingTags.length > 0 || data.matchingReports.length > 0)
    ) {
      setShowTypeahead(true);
    } else if (!data) {
      setShowTypeahead(false);
    }
  }, [data, focused]);

  const handleReportClick = (report: ReportTypeaheadDTO) => {
    if (onSelectReport) {
      onSelectReport(report);
    }
    setShowTypeahead(false);
  };

  const handleTagClick = (tag: DistinctTagDTO) => {
    if (onSelectTag) {
      onSelectTag(tag);
    }
    setShowTypeahead(false);
  };

  const handleClearClick = () => {
    if (onChange) {
      const event = {
        target: { value: "" },
      } as React.ChangeEvent<HTMLInputElement>;
      onChange(event);
    }
    inputRef.current?.focus();
  };

  return (
    <div
      css={css({
        position: "relative",
        display: "flex",
        alignItems: "center",
        width: focused ? "100%" : 280,
        background: "#fff",
        borderRadius: 4,
        boxShadow: focused
          ? `0 0 0 2px ${Colors.biDashboardView.FOCUS_COLOR}`
          : "0 1px 4px rgba(0,0,0,0.08)",
        transition:
          "box-shadow 0.2s cubic-bezier(.4,0,.2,1), width 0.3s cubic-bezier(.4,0,.2,1)",
        padding: "2px 12px 2px 36px",
      })}
    >
      <FontAwesomeIcon
        icon={faSearch}
        css={css({
          position: "absolute",
          left: 12,
          top: "50%",
          transform: `translateY(-50%) scale(${focused ? 1.2 : 1}) rotate(${
            focused ? 25 : 0
          }deg)`,
          color: focused ? Colors.biDashboardView.FOCUS_COLOR : "#b0b0b0",
          transition: "color 0.2s, transform 0.3s cubic-bezier(.4,0,.2,1)",
          pointerEvents: "none",
          animation: focused
            ? "spin360 0.6s cubic-bezier(.4,0,.2,1)"
            : undefined,
        })}
      />
      <input
        ref={inputRef}
        type="text"
        placeholder={t("Search...")}
        css={css({
          width: "100%",
          border: "none",
          outline: "none",
          fontSize: 16,
          background: "transparent",
          padding: "4px 0",
          color: "#222",
        })}
        value={value || ""}
        onChange={onChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        {...restProps}
      />
      {value && (
        <FontAwesomeIcon
          icon={faTimes}
          css={css({
            position: "absolute",
            right: 12,
            top: "50%",
            transform: "translateY(-50%)",
            color: "#b0b0b0",
            cursor: "pointer",
            fontSize: 14,
            "&:hover": {
              color: "#666",
            },
          })}
          onClick={handleClearClick}
        />
      )}

      {showTypeahead && (
        <div
          ref={typeaheadRef}
          css={css({
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            width: "100%",
            background: "#fff",
            borderRadius: 4,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            zIndex: 1000,
            maxHeight: 400,
            overflowY: "auto",
          })}
        >
          {isLoading ? (
            <div css={css({ padding: 12, textAlign: "center", color: "#999" })}>
              {t("Loading...")}
            </div>
          ) : (
            <>
              {data?.matchingTags && data.matchingTags.length > 0 && (
                <div>
                  <div
                    css={css({
                      padding: "8px 12px",
                      color: "#666",
                      background: "#f5f5f5",
                      fontSize: 14,
                      fontWeight: 700,
                    })}
                  >
                    {t("TAGS")}
                  </div>
                  <div
                    css={css({
                      display: "flex",
                      flexWrap: "wrap",
                      padding: "0 8px 8px",
                    })}
                  >
                    {data.matchingTags.map((tag) => (
                      <div
                        key={tag.tag}
                        css={css({
                          background: Colors.biDashboardView.TAG_BACKGROUND,
                          color: Colors.biDashboardView.TAG_TEXT_COLOR,
                          borderRadius: 16,
                          padding: "4px 12px",
                          margin: 4,
                          fontSize: 13,
                          cursor: "pointer",
                          "&:hover": {
                            background: shadeColor(
                              Colors.biDashboardView.TAG_BACKGROUND,
                              -0.05,
                            ),
                          },
                        })}
                        onClick={() => handleTagClick(tag)}
                      >
                        {tag.tag}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {data?.matchingReports && data.matchingReports.length > 0 && (
                <div>
                  <div
                    css={css({
                      padding: "8px 12px",
                      color: "#666",
                      background: "#f5f5f5",
                      fontSize: 14,
                      fontWeight: 700,
                    })}
                  >
                    {t("REPORTS")}
                  </div>
                  {data.matchingReports.map((report) => (
                    <div
                      key={report.id}
                      css={css({
                        padding: "8px 12px",
                        cursor: "pointer",
                        "&:hover": {
                          background: "#f5f5f5",
                        },
                      })}
                      onClick={() => handleReportClick(report)}
                    >
                      <div css={css({ fontWeight: 500 })}>{report.name}</div>
                      {report.details && (
                        <div
                          css={css({
                            fontSize: 13,
                            color: "#666",
                            marginTop: 2,
                          })}
                        >
                          {report.details}
                        </div>
                      )}
                      {report.tags && report.tags.length > 0 && (
                        <div
                          css={css({
                            display: "flex",
                            flexWrap: "wrap",
                            marginTop: 4,
                          })}
                        >
                          {report.tags.map((tag) => (
                            <div
                              key={tag.id}
                              css={css({
                                background:
                                  Colors.biDashboardView.TAG_BACKGROUND,
                                color: Colors.biDashboardView.TAG_TEXT_COLOR,
                                borderRadius: 12,
                                padding: "2px 8px",
                                margin: "2px 4px 2px 0",
                                fontSize: 11,
                              })}
                            >
                              {tag.tagName}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {(!data?.matchingTags || data.matchingTags.length === 0) &&
                (!data?.matchingReports ||
                  data.matchingReports.length === 0) && (
                  <div
                    css={css({
                      padding: 12,
                      textAlign: "center",
                      color: "#999",
                    })}
                  >
                    {t("No results found")}
                  </div>
                )}
            </>
          )}
        </div>
      )}

      <style>{`
        @keyframes spin360 {
          0% { transform: translateY(-50%) scale(${focused ? 1.2 : 1}) rotate(${
            focused ? 25 : 0
          }deg); }
          100% { transform: translateY(-50%) scale(${
            focused ? 1.2 : 1
          }) rotate(${focused ? 25 : 0}deg) rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default SearchInput;
